import { jsx as _jsx } from "react/jsx-runtime";
/**
 * RrThemeToggle — 3-state Light / Dark / System theme switch.
 *
 * T4_6-003. The control surface for the Product UI Standard §7 theme
 * cascade. Renders three buttons in a horizontal group (or vertical via
 * the `orientation` prop). The active mode reads `aria-pressed="true"`;
 * non-active modes read `aria-pressed="false"`. Buttons are reachable
 * via keyboard tab + Enter / Space.
 *
 * Visual treatment per Standard §3.2 (DM Mono primary-button type-role
 * + UPPERCASE + tracking) and §5.7 (button shape — radius-md + 0.5px
 * border + tinted active fill via --rr-accent-link). The toggle is a
 * compact button group rather than a select element because the three
 * states are mutually exclusive and visible-at-a-glance is the UX
 * priority — users glance at the user chip dropdown to confirm their
 * theme is what they expect.
 *
 * Consumers:
 *   - `<TokenSandbox>` (placed at the top of /_design/tokens; replaces
 *     the local-preview toggle from T4_6-002 once T4_6-CLEANUP removes
 *     the legacy component)
 *   - `<RrNav>` user-chip dropdown (T4_6-004; this component is the slot)
 *   - Future settings page (Phase 10A admin)
 */
import { useTheme } from '../hooks/useTheme.js';
const MODE_BUTTONS = [
    { mode: 'system', label: 'System' },
    { mode: 'light', label: 'Light' },
    { mode: 'dark', label: 'Dark' },
];
export function RrThemeToggle({ orientation = 'horizontal', className, ariaLabel = 'Theme mode', }) {
    const { mode, setTheme } = useTheme();
    const groupStyle = {
        display: 'inline-flex',
        flexDirection: orientation === 'vertical' ? 'column' : 'row',
        gap: 'var(--rr-space-2)',
    };
    return (_jsx("div", { role: "group", "aria-label": ariaLabel, className: className, style: groupStyle, children: MODE_BUTTONS.map(({ mode: btnMode, label }) => {
            const active = mode === btnMode;
            return (_jsx("button", { type: "button", "aria-pressed": active, onClick: () => setTheme(btnMode), style: buttonStyle(active), children: label }, btnMode));
        }) }));
}
/**
 * Inline styles only — no Tailwind utility surface (Tailwind doesn't
 * pick up CSS custom properties at compile time, and the toggle needs
 * to render correctly inside any consumer's container without
 * stylesheet ordering issues). Standard §5.7 primary-button shape +
 * §3.2 button-primary type role.
 */
function buttonStyle(active) {
    // E2E-R2 #3 — the active fill uses the THEME-STABLE brand teal
    // (--rr-color-teal #2A7B7E, ~5:1 with white in both themes) rather than
    // --rr-accent-link, which flips to a light cyan (#C2F0F1) in dark mode and
    // left white text unreadable on the active button. White text on the fixed
    // teal reads cleanly in light AND dark mode.
    return {
        fontFamily: 'var(--rr-font-mono)',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: 500,
        padding: '10px 16px',
        borderRadius: 'var(--rr-radius-md)',
        border: active
            ? '1px solid var(--rr-color-teal)'
            : '1px solid var(--rr-border-strong)',
        backgroundColor: active ? 'var(--rr-color-teal)' : 'transparent',
        color: active ? 'var(--rr-color-white)' : 'var(--rr-fg-primary)',
        cursor: 'pointer',
        transition: 'background-color var(--rr-duration-hover) ease, color var(--rr-duration-hover) ease, border-color var(--rr-duration-hover) ease',
    };
}
//# sourceMappingURL=RrThemeToggle.js.map